import { MonetaryValue } from 'ComponentLibrary/src/common/money'
import { isParkingType, type TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import { getTenantSummaryFromDetails } from 'ComponentLibrary/src/common/valuation-import/TenantSummary'
import type { TenantOverviewBreakdown } from 'ComponentLibrary/src/widgets/TenantTables/TenantSummaryTables/types'
import { previewYearlyRent, type PreviewTenant } from '../preview'

function aggregateTenants(tenants: PreviewTenant[], usageType: TenantUsageTypeLabelKeys): TenantOverviewBreakdown {
    const summary = getTenantSummaryFromDetails(
        tenants.map((tenant) => ({
            ...tenant,
            area: isParkingType(tenant.usage_type) ? undefined : tenant.area,
            rent_details: { yearly_rent: new MonetaryValue(previewYearlyRent(tenant)) },
            market_rent_details: tenant.market_rent
                ? { yearly_rent: new MonetaryValue(previewYearlyRent(tenant, tenant.market_rent)) }
                : undefined,
        }))
    )
    const area = summary.total_area.value
    const units = summary.number_of_units + summary.parking_units
    const vacantUnits = summary.vacancy_number_of_units + summary.vacant_parking_units
    const contractRent = MonetaryValue.fromMonetaryValueDataObject(summary.actual_rental_income)
    // A partial sum would look like a complete market rent for the category or total.
    const marketRent = tenants.every((tenant) => tenant.market_rent)
        ? MonetaryValue.fromMonetaryValueDataObject(summary.gross_rental_income)
        : undefined
    const rentBreakdown = (rent: MonetaryValue) => ({
        total: rent,
        per_sqm_monthly: area && usageType !== 'total' ? rent.divide(12 * area) : undefined,
        per_unit_monthly: units && usageType !== 'total' ? rent.divide(12 * units) : undefined,
    })
    return {
        usage_type: usageType,
        number_of_units: units,
        total_area: area ? summary.total_area : undefined,
        vacancy: {
            area: area ? summary.vacancy_area : undefined,
            units: vacantUnits,
            percentage: area ? summary.vacancy_area.value / area : units ? vacantUnits / units : 0,
        },
        actual_rent: rentBreakdown(contractRent),
        rent: marketRent ? rentBreakdown(marketRent) : undefined,
    }
}

export function tenantImportSummary(tenants: PreviewTenant[]): TenantOverviewBreakdown[] {
    const categories = new Map<PreviewTenant['usage_type'], PreviewTenant[]>()
    for (const tenant of tenants) {
        const group = categories.get(tenant.usage_type) ?? []
        group.push(tenant)
        categories.set(tenant.usage_type, group)
    }
    return [
        ...[...categories].map(([usageType, group]) => aggregateTenants(group, usageType)),
        ...(tenants.length ? [aggregateTenants(tenants, 'total')] : []),
    ]
}
